import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Command as CommandIcon, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../../ui/dialog';

export function ReportDraftCommandPalette({
  open,
  onOpenChange,
  sections,
  searchPlaceholder,
  emptyLabel,
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);

  const filteredSections = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const nextSections = sections
      .filter((section) => Array.isArray(section.items) && section.items.length > 0)
      .map((section) => {
        if (!normalizedQuery) {
          return section;
        }
        const filteredItems = section.items.filter((item) => {
          const searchableText = [
            section.label,
            item.label,
            item.shortcut,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return searchableText.includes(normalizedQuery);
        });
        return {
          ...section,
          items: filteredItems,
        };
      })
      .filter((section) => section.items.length > 0);

    return nextSections;
  }, [query, sections]);

  const flattenedItems = useMemo(
    () => filteredSections.flatMap((section) => section.items.map((item) => ({ section, item }))),
    [filteredSections]
  );

  useEffect(() => {
    if (!open) {
      setQuery('');
      setSelectedIndex(0);
      return;
    }
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [open]);

  useEffect(() => {
    setSelectedIndex((current) => {
      if (!flattenedItems.length) {
        return 0;
      }
      return Math.min(current, flattenedItems.length - 1);
    });
  }, [flattenedItems.length]);

  const handleSelect = (item) => {
    if (!item || typeof item.onSelect !== 'function') {
      return;
    }
    item.onSelect();
    onOpenChange(false);
  };

  const handleKeyDown = (event) => {
    if (!open) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!flattenedItems.length) {
        return;
      }
      setSelectedIndex((current) => (current + 1) % flattenedItems.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!flattenedItems.length) {
        return;
      }
      setSelectedIndex((current) => (current - 1 + flattenedItems.length) % flattenedItems.length);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      handleSelect(flattenedItems[selectedIndex]?.item);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      onOpenChange(false);
    }
  };

  let itemCursor = 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="report-draft-editor__command-palette-dialog"
        showCloseButton
        onKeyDown={handleKeyDown}
      >
        <DialogTitle className="sr-only">{t('reports.review.editor.commandPalette.menu')}</DialogTitle>
        <DialogDescription className="sr-only">
          {t('reports.review.editor.commandPalette.searchPlaceholder')}
        </DialogDescription>
        <div className="report-draft-editor__command-palette-head">
          <div className="report-draft-editor__command-palette-title-wrap">
            <CommandIcon size={16} />
            <span className="report-draft-editor__command-palette-title">{t('reports.review.editor.toolbar.commandPaletteShort')}</span>
          </div>
        </div>

        <div className="report-draft-editor__command-palette-search">
          <Search size={15} aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setSelectedIndex(0);
            }}
            placeholder={searchPlaceholder}
            className="report-draft-editor__command-palette-input"
          />
        </div>

        <div className="report-draft-editor__command-palette-list" role="listbox" aria-label={searchPlaceholder}>
          {filteredSections.length === 0 ? (
            <div className="report-draft-editor__command-palette-empty">{emptyLabel}</div>
          ) : (
            filteredSections.map((section) => {
              const sectionStartIndex = itemCursor;
              itemCursor += section.items.length;
              return (
                <div key={section.id} className="report-draft-editor__command-palette-section">
                  {section.label ? (
                    <div className="report-draft-editor__command-palette-section-label">{section.label}</div>
                  ) : null}
                  <div className="report-draft-editor__command-palette-section-items">
                    {section.items.map((item, itemIndex) => {
                      const flatIndex = sectionStartIndex + itemIndex;
                      const isSelected = flatIndex === selectedIndex;
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          className={`report-draft-editor__command-palette-item${isSelected ? ' is-active' : ''}`}
                          onMouseEnter={() => setSelectedIndex(flatIndex)}
                          onMouseDown={(event) => {
                            event.preventDefault();
                            handleSelect(item);
                          }}
                        >
                          <span className="report-draft-editor__command-palette-item-main">
                            {Icon ? (
                              <span className="report-draft-editor__command-palette-item-icon" aria-hidden="true">
                                <Icon size={15} />
                              </span>
                            ) : null}
                            <span className="report-draft-editor__command-palette-item-label">{item.label}</span>
                          </span>
                          {item.shortcut ? (
                            <span className="report-draft-editor__command-palette-item-shortcut">
                              {item.shortcut}
                            </span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

ReportDraftCommandPalette.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  sections: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      shortcut: PropTypes.string,
      icon: PropTypes.elementType,
      onSelect: PropTypes.func.isRequired,
    })).isRequired,
  })).isRequired,
  searchPlaceholder: PropTypes.string.isRequired,
  emptyLabel: PropTypes.string.isRequired,
};
